import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, BookText, Check, Copy, KeyRound, Loader2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import Card from '../../components/Card';
import Modal from '../../components/Modal';
import { createServiceKey, getServiceKeys, revokeServiceKey } from './serviceKeysApi';

// Service API keys (doc-33) — admin panel for the machine credentials behind
// the read-only /api/v1 surface. The plaintext secret is shown exactly once,
// in the create dialog; after that only the display prefix identifies a key.

// The scopes a key can be granted, mirroring SERVICE_KEY_SCOPES in
// backend/services/service-api-keys-service.js (issuance rejects anything
// else). Only read:structured_data is pre-checked: read:documents (doc-289)
// serves whole agreements — extracted text and raw bytes — which is a
// different sensitivity class, so it is a deliberate opt-in per key.
export const SCOPE_OPTIONS = [
  {
    scope: 'read:structured_data',
    label: 'Structured data',
    hint: 'Current contract fields, schema catalog, and extracted outputs with verification and provenance.',
    defaultOn: true,
  },
  {
    scope: 'read:documents',
    label: 'Documents',
    hint: 'The extracted plain text and the raw file of each contract’s copy of record.',
    defaultOn: false,
  },
  {
    scope: 'read:snapshot',
    label: 'Snapshot',
    hint: 'Every contract, request, review, schema output and file, restricted ones included, for loading a local Docket. Mint for one pull and revoke after.',
    defaultOn: false,
  },
];

const DEFAULT_SCOPES = SCOPE_OPTIONS.filter((o) => o.defaultOn).map((o) => o.scope);

function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function CopyKeyField({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable — the key is still selectable */ }
  };
  return (
    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
      <code
        className="mono"
        style={{
          flex: 1, minWidth: 0, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.5,
          background: 'var(--surface-secondary)', border: '0.5px solid var(--border-primary)',
          borderRadius: 6, overflowWrap: 'anywhere', userSelect: 'all',
        }}
      >
        {value}
      </code>
      <button type="button" className="btn outline sm" onClick={copy}>
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

export default function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(DEFAULT_SCOPES);
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // { key, record } — plaintext lives only here
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [modalError, setModalError] = useState('');

  const refresh = useCallback(async () => {
    try {
      const res = await getServiceKeys();
      setKeys(res.keys || []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const closeCreate = () => {
    setCreateOpen(false);
    setCreatedKey(null);
    setName('');
    setScopes(DEFAULT_SCOPES);
    setModalError('');
  };

  const toggleScope = (scope) => {
    setScopes((prev) => (prev.includes(scope)
      ? prev.filter((s) => s !== scope)
      : SCOPE_OPTIONS.map((o) => o.scope).filter((s) => prev.includes(s) || s === scope)));
  };

  const handleCreate = async () => {
    setCreating(true);
    setModalError('');
    try {
      const res = await createServiceKey({ name, scopes });
      setCreatedKey(res);
      refresh();
    } catch (err) {
      setModalError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeServiceKey(revokeTarget.id);
      setRevokeTarget(null);
      refresh();
    } catch (err) {
      setError(err.message);
      setRevokeTarget(null);
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Card
      title="Service API keys"
      icon={KeyRound}
      action={(
        <>
          {/* Admins land here to mint a key; the how-do-I-use-it guide is one
              click away (doc-277). */}
          <Link className="btn outline sm" to="/docs/structured-data-api">
            <BookText className="w-3.5 h-3.5" />
            API documentation
          </Link>
          <button type="button" className="btn primary sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-3.5 h-3.5" />
            New key
          </button>
        </>
      )}
      flush
    >
      <div style={{ padding: '12px 24px 0' }}>
        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
          Machine credentials for the read-only REST API (<code className="mono" style={{ fontSize: 12 }}>/api/v1</code>) —
          current contract fields, schemas and their extracted outputs, with verification and provenance so consumers can filter to
          verified-only data, plus each contract’s document text and file where a key is scoped for it.
          Send a key as <code className="mono" style={{ fontSize: 12 }}>Authorization: Bearer …</code>.
          The secret is shown once at creation; revoking takes effect immediately.
        </div>
      </div>

      {error && (
        <div className="callout red" style={{ margin: '12px 24px 0' }}>
          <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
          <div className="callout-body">{error}</div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
        </div>
      ) : keys.length === 0 ? (
        <div className="muted" style={{ padding: '20px 24px 24px', fontSize: 12.5 }}>
          No API keys yet. Create one to give a pipeline read access to structured data.
        </div>
      ) : (
        <table className="tbl" style={{ marginTop: 14 }}>
          <thead>
            <tr>
              <th style={{ paddingLeft: 24 }}>Name</th>
              <th>Key</th>
              <th>Scopes</th>
              <th>Created</th>
              <th>Last used</th>
              <th style={{ paddingRight: 24, width: 110 }} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} style={k.revokedAt ? { opacity: 0.6 } : undefined}>
                <td style={{ paddingLeft: 24, fontWeight: 500 }}>
                  {k.name}
                  {k.revokedAt && <span className="chip sq gray" style={{ marginLeft: 8 }}>Revoked</span>}
                </td>
                <td><code className="mono" style={{ fontSize: 12 }}>{k.keyPrefix}…</code></td>
                <td className="muted" style={{ fontSize: 12 }}>{(k.scopes || []).join(', ') || '—'}</td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {fmtDateTime(k.createdAt)}
                  {k.createdByName ? ` by ${k.createdByName}` : ''}
                </td>
                <td className="muted" style={{ fontSize: 12 }}>{k.lastUsedAt ? fmtDateTime(k.lastUsedAt) : 'Never'}</td>
                <td style={{ paddingRight: 24, textAlign: 'right' }}>
                  {!k.revokedAt && (
                    <button
                      type="button"
                      className="btn ghost sm"
                      style={{ color: 'var(--text-critical)' }}
                      onClick={() => setRevokeTarget(k)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal
        isOpen={createOpen}
        onClose={closeCreate}
        title={createdKey ? 'API key created' : 'New service API key'}
        description={createdKey
          ? 'Copy the key now — this is the only time it will be shown.'
          : 'Name the key after the system that will use it.'}
        footer={createdKey ? (
          <button type="button" className="btn primary sm" onClick={closeCreate}>Done</button>
        ) : (
          <>
            <button type="button" className="btn ghost sm" onClick={closeCreate}>Cancel</button>
            <button
              type="button"
              className="btn primary sm"
              onClick={handleCreate}
              disabled={creating || !name.trim() || scopes.length === 0}
            >
              {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create key
            </button>
          </>
        )}
      >
        {createdKey ? (
          <div className="card-body stack-sm">
            <CopyKeyField value={createdKey.key} />
            <div className="callout yellow">
              <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
              <div className="callout-body" style={{ fontSize: 12.5 }}>
                Docket stores only a hash. If this key is lost, revoke it and create a new one.
              </div>
            </div>
          </div>
        ) : (
          <div className="card-body stack-sm">
            {modalError && (
              <div className="callout red">
                <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
                <div className="callout-body">{modalError}</div>
              </div>
            )}
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Data science pipeline"
              aria-label="Key name"
              autoFocus
            />
            <div>
              <div className="muted" style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                Scopes
              </div>
              <div className="stack-sm">
                {SCOPE_OPTIONS.map((o) => (
                  <label
                    key={o.scope}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10,
                      borderRadius: 6, border: '0.5px solid var(--border-primary)',
                      background: 'var(--surface-base)', cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={scopes.includes(o.scope)}
                      onChange={() => toggleScope(o.scope)}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                        {o.label}
                        <code className="mono" style={{ fontSize: 11.5, marginLeft: 6, color: 'var(--text-tertiary)' }}>{o.scope}</code>
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.45 }}>
                        {o.hint}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              Access is read-only and spans all contracts the API exposes. Every read is recorded in
              the audit log under this key.
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke API key?"
        description={revokeTarget ? `"${revokeTarget.name}" (${revokeTarget.keyPrefix}…) will stop working immediately. This cannot be undone.` : ''}
        footer={(
          <>
            <button type="button" className="btn ghost sm" onClick={() => setRevokeTarget(null)}>Cancel</button>
            <button type="button" className="btn danger sm" onClick={handleRevoke} disabled={revoking}>
              {revoking && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Revoke key
            </button>
          </>
        )}
      />
    </Card>
  );
}
